import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { apiService } from '../../api/axiosClient';
import moment from 'moment';
import { useEffect, useState } from 'react';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import CouponToolbar from './CouponToolbar';

const TableCoupon = props => {
  const {reload, setReload, setCouponID} = props;
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [list, setList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [sortColumn, setSortColumn] = useState('cp_id');
  const [selected, setSelected] = useState([]);

  const map_type = {1: "다운로드", 2: "자동발행", 3: "수동발행"};
  const map_use = {1: "금액할인", 2: "배송비", 3: "포인트"};

  const loadCoupons = async (sort, query, column) => {
      try {
        const response = await apiService.get('/coupons', {
          params: {
            query,
            expire: false,
            sort,
            column,
            page,
            pageSize
          }
        })
        const payload = response.data;
        setTotal(payload.size)
        setList(payload.list)
        
        setLoading(false)
      } catch (e) {
        console.error(e)
      }
    }
    
  useEffect(() => {
    loadCoupons(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadCoupons(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('cp_id')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadCoupons(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      flex: 0.1,
      minWidth: 50,
      field: 'cp_id',
      headerName: '쿠폰 ID',
      renderCell: params => (
        <a href='#' onClick={() => setCouponID(params.row.cp_id)} >
          <CustomChip
            size='small'
            skin='light'
            color='primary'
            label={params.row.cp_id}
            sx={{ '& .MuiChip-label': { textTransform: 'capitalize' }, padding: 2, cursor: 'pointer' }}
          />
        </a>
      )
    },
    {
      flex: 0.2,
      minWidth: 110,
      field: 'cp_title',
      headerName: '이름',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.cp_title}
        </Typography>
      )
    },
    {
      flex: 0.1,
      minWidth: 110,
      field: 'cp_type',
      headerName: '쿠폰유형',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {map_type[params.row.cp_type]}
        </Typography>
      )
    },
    {
      flex: 0.1,
      minWidth: 110,
      field: 'cp_category',
      headerName: '분류',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {map_use[params.row.cp_category]}
        </Typography>
      )
    },
    {
      flex: 0.1,
      minWidth: 110,
      field: 'cp_product',
      headerName: '상품코드',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.cp_product}
        </Typography>
      )
    },
    {
      flex: 0.2,
      minWidth: 110,
      field: 'duration',
      headerName: '유효기간',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.cp_start} ~ {params.row.cp_end}
        </Typography>
      )
    },    
    {
      flex: 0.1,
      field: 'cp_datetime',
      minWidth: 140,
      headerName: '발급날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {moment(params.row.cp_datetime).format("YYYY-MM-DD")}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='쿠폰발급 내역' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.cp_id}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: CouponToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.cp_id),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableCoupon
